import React, { useMemo, useState } from 'react';
import { pick } from 'ramda';
import { Menu } from '@theme/js/components/main/Menu';
import { Header } from '@theme/js/components/main/Header';
import { Footer } from '@theme/js/components/main/Footer';
import { Reconnect } from '@core/js/components/main/Reconnect';
import { SlideMenu } from '@core/js/components/main/SlideMenu';
import { BACKBONE_APP_ROOT } from '@core/js/backbone/constants';
import { AboveHeader } from '@theme/js/components/main/AboveHeader';
import { WebContent } from '@core/js/components/system/WebContent';
import { useAppSelector, useDeviceSelector, usePageSelector } from '@core/js/store/hooks';
import { useUpdateHeaderHeight } from '@core/js/components/system/App/hooks';
import { MyAccountSidebar } from '@theme/js/components/main/MyAccountSidebar';
import { SignInNotificationBanner } from '@theme/js/components/main/SignInNotificationBanner';
import { CookiesRegulationMessage } from '@theme/js/components/main/CookiesRegulationMessage';
import { GoTopButton } from '@theme/js/components/main/GoTopButton'; /* Lugano changes */
import { GlobalSearch } from '@theme/js/components/main/GlobalSearch'; /* Lugano changes */
import { HeaderBottomSection } from '@theme/js/components/main/HeaderBottomSection'; /* Lugano changes */
import { getURLParam } from '@theme/js/utils/url.util'; /* Lugano changes */

import { PageRouter } from '@core/js/components/system/Router/components/PageRouter';
import { LAYER_POSITION } from '@core/js/components/system/Router/constants';

import style from './AppRouter.scss';

/* Lugano changes */
const CookieLayer = ({ position, cookiesRef }) => {
    const layerClass = position === LAYER_POSITION.TOP ? style.cookieLayerTop : style.cookieLayerBottom;
    return (
        <div className={layerClass}>
            <CookiesRegulationMessage cookiesRef={cookiesRef} />
        </div>
    );
};
/* Lugano changes */

/**
 * Application Router that is aware about business logic.
 */
export const AppRouter = () => {
    const [isVisibleNotification, setIsVisibleNotification] = useState(false);
    const { isMobile, formFactor } = useDeviceSelector(pick(['isMobile', 'formFactor'])); /* Lugano changes */
    const {
        aboveHeaderWcIdDesktop, /* Lugano changes */
        aboveHeaderWcIdTablet, /* Lugano changes */
        applicationWCID,
        headerHeight,
        cookiesConfiguration: { layerPosition, enableCookiesAdmission }
    } = useAppSelector(pick(['aboveHeaderWcIdDesktop', 'aboveHeaderWcIdTablet', 'applicationWCID', 'headerHeight', 'cookiesConfiguration']));
    const { headerWcId, overrideHeader } = usePageSelector(pick(['headerWcId', 'overrideHeader'])); /* Lugano changes */
    const hideHeaderByUrl = getURLParam(location.search, 'hideHeader') === '1';
    const hideDesktopHeaderByUrl = getURLParam(location.search, 'showHeaderOnlyOnMobile') === '1';
    const hideHeader = hideHeaderByUrl || hideDesktopHeaderByUrl && !isMobile || overrideHeader && !headerWcId; /* Lugano changes */

    /* Lugano changes */
    const aboveHeaderWcID = {
        desktop: aboveHeaderWcIdDesktop,
        tablet: aboveHeaderWcIdTablet
    }[formFactor];

    const cookieLayerPosition = layerPosition === LAYER_POSITION.TOP
        ? LAYER_POSITION.TOP
        : LAYER_POSITION.BOTTOM;

    const layerClass = layerPosition === LAYER_POSITION.TOP || aboveHeaderWcID
        ? style.notificationLayerTop
        : style.notificationLayerBottom;

    const cookiesRef = useUpdateHeaderHeight();

    const shouldApplyHeaderHeight = useMemo(() => {
        const isTop = layerPosition === LAYER_POSITION.TOP;
        const isBottom = layerPosition === LAYER_POSITION.BOTTOM;

        return !enableCookiesAdmission
            || isMobile && enableCookiesAdmission && isBottom
            || isTop
            || !isVisibleNotification && isTop
            || isVisibleNotification && isBottom;
    }, [enableCookiesAdmission, isMobile, isVisibleNotification, layerPosition]);

    const contentInlineCss = useMemo(() => ({
        paddingTop: `${shouldApplyHeaderHeight || aboveHeaderWcID ? headerHeight : '0'}px`,
        '--scroll-margin-top': `${shouldApplyHeaderHeight || aboveHeaderWcID ? headerHeight : '0'}px`
    }), [aboveHeaderWcID, headerHeight, shouldApplyHeaderHeight]);

    if (applicationWCID) {
        return <WebContent id={applicationWCID} />;
    }

    const renderNotificationLayer = () =>
        !isMobile && (
            <div
                className={`${style.notification} ${isVisibleNotification ? style.notificationActive : ''} ${layerClass}`}
                {...(
                    (!isVisibleNotification && layerPosition === LAYER_POSITION.TOP || aboveHeaderWcID)
                    && { ref: cookiesRef }
                )}
            >
                <SignInNotificationBanner
                    aboveHeaderWcID={aboveHeaderWcID}
                    setIsVisibleNotification={setIsVisibleNotification}
                    cookiePosition={cookieLayerPosition}
                    notificationRef={cookiesRef}
                />
                <CookieLayer
                    position={cookieLayerPosition}
                    {...(!isVisibleNotification && layerPosition === LAYER_POSITION.BOTTOM && !aboveHeaderWcID && { cookiesRef })}
                />
                <AboveHeader/>
            </div>
        )
    ;
    /* Lugano changes */

    /* Lugano changes */
    return (
        <>
            {isMobile && <SlideMenu />}
            <MyAccountSidebar />
            {renderNotificationLayer()}
            <div className={`${style.header} ${hideHeader ? style.hideHeader : ''} header`} ref={isMobile ? cookiesRef : null}>
                {isMobile && <SignInNotificationBanner/>}
                {isMobile && <CookieLayer position={cookieLayerPosition}/>}
                {isMobile && <AboveHeader/>}
                {!hideHeader && <div className={BACKBONE_APP_ROOT}>
                    <div className="fn-navigation-container navigation-container">
                        <div className="lgn-page-header">
                            <Header/>
                            <Menu/>
                            {!isMobile && <HeaderBottomSection/>}
                        </div>
                    </div>
                </div>}
            </div>
            <div className={`${style.content} ${hideHeader ? style.withOutHeader : ''}`} style={contentInlineCss} >
                <Reconnect />
                <PageRouter />
            </div>
            {isMobile && <GlobalSearch listenGlobalSearchEvent />}
            <Footer />
            <GoTopButton />
        </>
    );
    /* Lugano changes */
};
